// NavigationAppBar.tsx
import * as React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box, Typography, AppBar, Toolbar, Button, IconButton, Container, Divider, List, ListItem, ListItemButton, ListItemAvatar, Drawer, Tooltip, Zoom } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import LanguagePicker from './LanguagePicker';
import { AppBarNavigationProps } from '../types/navigationProperties';
import { useTranslation } from 'react-i18next';
import { PaginatedResponse } from '../types/pagination';
import { Project } from '../services/ProjectsServiceProvider';
import logo_black_tp from '../assets/img/logo_black_tp.png';
import logo_white_tp from '../assets/img/logo_white_tp.png';
import {
    useNavigate
} from "react-router-dom";
import { keyframes } from '@mui/system';
import { useColorScheme } from '@mui/material/styles';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
    backdropFilter: 'blur(24px)',
    border: '1px solid',
    borderColor: (theme.vars || theme).palette.divider,
    backgroundColor: theme.vars
        ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
        : alpha(theme.palette.background.default, 0.4),
    boxShadow: (theme.vars || theme).shadows[1],
    padding: '8px 12px',
}));

export const pulse = keyframes`
  0% {
    transform: scale( .75 );
  }
  20% {
    transform: scale( 1 );
  }
  40% {
    transform: scale( .75 );
  }
  60% {
    transform: scale( 1 );
  }
  80% {
    transform: scale( .75 );
  }
  100% {
    transform: scale( .75 );
  }
`;

/*
227 and 239 - line theme is disabled
                        <Tooltip title={t("color.picker")}
                                 placement="bottom"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <ColorModeIconDropdown mode={mode} systemMode={systemMode} />
                        </Tooltip>

*/

export default function AppAppBar({ props, pageName }: { props: AppBarNavigationProps, pageName: string }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const projects: PaginatedResponse<Project> | undefined = props.projects;
    const projectsLoading: boolean = props.loading;
    const navigate = useNavigate();
    const { mode, systemMode } = useColorScheme();

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen);
    };

    const navigateTo = (page: string) => {
        navigate(page);
        setOpen(false);
    };

    if(projectsLoading) {
        return (
            <div></div>
        )
    }

    return (
        <AppBar
            position="fixed"
            enableColorOnDark
            sx={{
                boxShadow: 0,
                bgcolor: 'transparent',
                backgroundImage: 'none',
                mt: 'calc(var(--template-frame-height, 0px) + 10px)',
            }}>
            <Container maxWidth="lg">
                <StyledToolbar variant="dense" disableGutters>
                    <Box
                        sx={{
                            gap: 1,
                            alignItems: 'center',
                            marginRight: 2,
                        }}>
                        <Box onClick={() => navigateTo("/")} sx={{ cursor: 'pointer'}}>
                            <Tooltip title={t("home.title")}
                                     placement="bottom"
                                     slots={{
                                        transition: Zoom,
                                     }}
                                     slotProps={{
                                        transition: { timeout: 300 },
                                     }} arrow>
                                {pageName !== 'home' ? (
                                    <Box sx={{ animation: `${pulse} 1s ease-in-out infinite` }}>
                                        <img loading="lazy" style={{ borderRadius: 'full', width: '60px' }}
                                               src={mode === 'dark' ? logo_white_tp : logo_black_tp}
                                               alt='logo'/>
                                    </Box>
                                ) : (
                                    <img loading="lazy" style={{ borderRadius: 'full', width: '60px' }}
                                           src={mode === 'dark' ? logo_white_tp : logo_black_tp}
                                           alt='logo'/>
                                )}
                            </Tooltip>
                        </Box>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
                        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                            {projects !== undefined && projects.results.map((item) => (
                                <Box onClick={() => navigateTo(`projects/${item.slug}`)}
                                     key={item.slug} sx={{ cursor: 'pointer'}}>
                                    <Button variant="text" color="secondary" size="small">
                                        {mode === 'light' && item.logo ? (
                                            <img loading="lazy" src={item.logo}
                                                   style={{ height: '23px' }}
                                                   alt={item.title.value}/>
                                        ) : mode === 'dark' && item.logo_dark ? (
                                            <img loading="lazy" src={item.logo_dark}
                                                   style={{ height: '23px' }}
                                                   alt={item.title.value}/>
                                        ) : (
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {item.title.value}
                                            </Typography>
                                        )}
                                    </Button>
                                </Box>
                            ))}
                            <Box onClick={() => navigateTo("/custom-shop")} sx={{ cursor: 'pointer'}}>
                                <Button variant="text" color="secondary" size="small"
                                    sx={{ '&:hover': {
                                        borderBottom: '1px solid black'
                                        } }}>
                                    <Typography sx={{ fontWeight: 'semibold' }}>
                                        {t('custom_shop.menu.link')}
                                    </Typography>
                                </Button>
                            </Box>
                            <Box onClick={() => navigateTo("/guitar-service")} sx={{ cursor: 'pointer'}}>
                                <Button variant="text" color="secondary" size="small"
                                    sx={{ '&:hover': {
                                        borderBottom: '1px solid black'
                                        } }}>
                                    <Typography sx={{ fontWeight: 'semibold' }}>
                                        {t('guitar_service.menu.link')}
                                    </Typography>
                                </Button>
                            </Box>
                            <Box onClick={() => navigateTo("/video-presentation")} sx={{ cursor: 'pointer'}}>
                                <Button variant="text" color="secondary" size="small"
                                    sx={{ '&:hover': {
                                        borderBottom: '1px solid black'
                                        } }}>
                                    <Typography sx={{ fontWeight: 'semibold' }}>
                                        {t('video_presentations.menu.link')}
                                    </Typography>
                                </Button>
                            </Box>
                            <Box onClick={() => navigateTo("/about-us")} sx={{ cursor: 'pointer'}}>
                                <Button variant="text" color="secondary" size="small"
                                    sx={{ '&:hover': {
                                        borderBottom: '1px solid black'
                                        } }}>
                                    <Typography sx={{ fontWeight: 'semibold' }}>
                                        {t('about.menu.link')}
                                    </Typography>
                                </Button>
                            </Box>
                            <Box onClick={() => navigateTo("/contact-us")} sx={{ cursor: 'pointer'}}>
                                <Button variant="text" color="secondary" size="small"
                                    sx={{ '&:hover': {
                                        borderBottom: '1px solid black'
                                        } }}>
                                    <Typography sx={{ fontWeight: 'semibold' }}>
                                        {t('contact.menu.link')}
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            gap: 1,
                            alignItems: 'center',
                        }}>
                        <Tooltip title={t("language.picker")}
                                 placement="bottom"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <LanguagePicker {...props} />
                        </Tooltip>
                        <Tooltip title={t("color.picker")}
                                 placement="bottom"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <ColorModeIconDropdown mode={mode} systemMode={systemMode} />
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
                        <Tooltip title={t("language.picker")}
                                 placement="bottom"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <LanguagePicker {...props} />
                        </Tooltip>
                        <Tooltip title={t("color.picker")}
                                 placement="bottom"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <ColorModeIconDropdown mode={mode} systemMode={systemMode} />
                        </Tooltip>
                        <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
                            <Tooltip title={t("menu.title")}
                                     placement="bottom"
                                     slots={{
                                        transition: Zoom,
                                     }}
                                     slotProps={{
                                        transition: { timeout: 300 },
                                     }} arrow>
                                <MenuIcon />
                            </Tooltip>
                        </IconButton>
                        <Drawer
                            anchor="top"
                            open={open}
                            onClose={toggleDrawer(false)}
                            PaperProps={{
                                sx: {
                                    top: 'var(--template-frame-height, 0px)',
                                },
                            }}>
                            <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}>
                                    <IconButton onClick={toggleDrawer(false)}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Box>
                                <List>
                                    {projects !== undefined && projects.results.map((item) => (
                                        <Box onClick={() => navigateTo(`projects/${item.slug}`)}
                                             key={item.slug} sx={{ cursor: 'pointer'}}>
                                            <ListItem disablePadding>
                                                <ListItemButton>
                                                    {item.logo ? (
                                                        <ListItemAvatar>
                                                            <img loading="lazy" src={item.logo}
                                                                   style={{ height: '50px' }}
                                                                   alt={item.title.value}/>
                                                        </ListItemAvatar>
                                                    ) : (
                                                        <Button variant="text" color="primary" size="small"
                                                            sx={{ '&:hover': {
                                                                borderBottom: '1px solid black'
                                                                } }}>
                                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                                {item.title.value}
                                                            </Typography>
                                                        </Button>
                                                    )}
                                                </ListItemButton>
                                            </ListItem>
                                        </Box>
                                    ))}
                                <Divider sx={{ my: 1 }} />
                                    <Box onClick={() => navigateTo("/custom-shop")} sx={{ cursor: 'pointer'}}>
                                        <Button variant="text" color="primary" size="small"
                                            sx={{ '&:hover': {
                                                borderBottom: '1px solid black'
                                                } }}>
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {t('custom_shop.menu.link')}
                                            </Typography>
                                        </Button>
                                    </Box>
                                    <Box onClick={() => navigateTo("/guitar-service")} sx={{ cursor: 'pointer'}}>
                                        <Button variant="text" color="primary" size="small"
                                            sx={{ '&:hover': {
                                                borderBottom: '1px solid black'
                                                } }}>
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {t('guitar_service.menu.link')}
                                            </Typography>
                                        </Button>
                                    </Box>
                                    <Box onClick={() => navigateTo("/video-presentation")} sx={{ cursor: 'pointer'}}>
                                        <Button variant="text" color="primary" size="small"
                                            sx={{ '&:hover': {
                                                borderBottom: '1px solid black'
                                                } }}>
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {t('video_presentations.menu.link')}
                                            </Typography>
                                        </Button>
                                    </Box>
                                    <Box onClick={() => navigateTo("/about-us")} sx={{ cursor: 'pointer'}}>
                                        <Button variant="text" color="primary" size="small"
                                            sx={{ '&:hover': {
                                                borderBottom: '1px solid black'
                                                } }}>
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {t('about.menu.link')}
                                            </Typography>
                                        </Button>
                                    </Box>
                                    <Box onClick={() => navigateTo("/contact-us")} sx={{ cursor: 'pointer'}}>
                                        <Button variant="text" color="primary" size="small"
                                            sx={{ '&:hover': {
                                                borderBottom: '1px solid black'
                                                } }}>
                                            <Typography sx={{ fontWeight: 'semibold' }}>
                                                {t('contact.menu.link')}
                                            </Typography>
                                        </Button>
                                    </Box>
                                </List>
                                <Divider sx={{ my: 3 }} />
                            </Box>
                        </Drawer>
                    </Box>
                </StyledToolbar>
            </Container>
        </AppBar>
    );
}